import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "lib/supabase";
import { useSession } from "contexts/SessionContext";

// État partagé des veilles. La liste vient de Supabase et est filtrée par la RLS :
// un analyste ne reçoit QUE les veilles auxquelles l'admin lui a donné accès — il ne
// voit donc jamais une veille non autorisée, sur aucune page.
// Aucune donnée de démonstration n'est mélangée : une veille affiche uniquement ses
// vraies statistiques moteur (vue veille_stats), et un état vide honnête tant qu'aucune
// retombée n'a été collectée.
const VeillesContext = createContext(null);

// "Il y a 2 h" / "Hier" / "12 juin" — affichage des dates de retombées
export function dateRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffH = Math.floor(diffMs / 36e5);
  if (diffH < 1) return "Il y a moins d'une heure";
  if (diffH < 24) return `Il y a ${diffH} h`;
  const diffJ = Math.floor(diffH / 24);
  if (diffJ === 1) return "Hier";
  if (diffJ < 7) return `Il y a ${diffJ} j`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

function pct(part, total) {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

// Construit l'objet veille consommé par les vues, EXCLUSIVEMENT depuis les données réelles
// (ligne `veilles` + stats moteur `veille_stats`). Aucune donnée de démonstration.
function mapRow(row, stats) {
  const s = stats || {};
  const hasRealData = (s.mentions || 0) > 0;
  const base = {
    id: row.id, // UUID Supabase = identifiant faisant foi (routing, accès)
    nom: row.nom || "Veille sans nom",
    client: row.client || "",
    secteur: row.secteur || "",
    statut: row.statut || "programmee",
    frequence: row.frequence || null,
    format: row.format || null,
    motsCles: Array.isArray(row.mots_cles) ? row.mots_cles : [],
    canaux: (row.veille_canaux || []).map((c) => c.canal_id),
    sources: 0,
    retombees: [],
    mentions: 0,
    couverture: 0,
    sentiment: { positif: 0, neutre: 0, negatif: 0 },
    responsable: "—",
    prochaineDate: row.prochaine_execution_at
      ? new Date(row.prochaine_execution_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })
      : "—",
    hasRealData,
  };
  if (!hasRealData) return base;
  // Statistiques moteur réelles (chargées à la demande via fetchMentions pour le détail)
  return {
    ...base,
    mentions: s.mentions,
    sources: s.sources,
    portee: s.portee || 0,
    derniereMention: s.derniere_mention,
    derniereDate: dateRelative(s.derniere_mention),
    sentiment: {
      positif: pct(s.positif, s.mentions),
      negatif: pct(s.negatif, s.mentions),
      neutre: Math.max(0, 100 - pct(s.positif, s.mentions) - pct(s.negatif, s.mentions)),
    },
  };
}

// Dernières mentions réelles d'une veille, au format « retombée » attendu par les vues
export async function fetchMentions(veilleId, limit = 20) {
  const { data, error } = await supabase
    .from("mentions")
    .select("id, titre, url, extrait, auteur, tonalite, canal_id, published_at, detected_at, sources(nom)")
    .eq("veille_id", veilleId)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) return [];
  return (data || []).map((m) => ({
    id: m.id,
    titre: m.titre,
    url: m.url,
    extrait: m.extrait,
    auteur: m.auteur,
    source: (m.sources && m.sources.nom) || "—",
    canal: m.canal_id || "web",
    date: dateRelative(m.published_at || m.detected_at),
    tonalite: m.tonalite || "neutre",
  }));
}

// Rapports produits par le moteur pour une veille (métadonnées + URL signée à la demande)
export async function fetchReports(veilleId) {
  const { data, error } = await supabase
    .from("reports")
    .select("id, titre, fichier_format, format_contenu, periode_debut, periode_fin, storage_path, taille_octets, created_at")
    .eq("veille_id", veilleId)
    .order("created_at", { ascending: false });
  if (error) return [];
  return data || [];
}

// Config complète d'une veille pour le mode édition (destinataires, mapping, heure, jours RÉELS).
// mapRow n'expose pas ces champs ; sans ce chargement, sauvegarder une édition écraserait
// les destinataires/mapping par des valeurs par défaut.
export async function fetchVeilleConfig(veilleId) {
  const [{ data: v }, { data: recips }, { data: mappingRows }] = await Promise.all([
    supabase.from("veilles").select("heure_envoi, jours").eq("id", veilleId).maybeSingle(),
    supabase.from("veille_recipients").select("email").eq("veille_id", veilleId),
    supabase.from("mapping_documents").select("contenu").eq("veille_id", veilleId).limit(1),
  ]);
  return {
    heure: v && v.heure_envoi ? String(v.heure_envoi).slice(0, 5) : "08:00",
    jours: (v && v.jours) || [],
    destinataires: (recips || []).map((r) => r.email).filter(Boolean),
    mapping: (mappingRows && mappingRows[0] && mappingRows[0].contenu) || "",
  };
}

// Dernier job de génération de matrice d'une veille (écran d'attente)
export async function fetchMatriceJob(veilleId) {
  const { data, error } = await supabase
    .from("matrice_jobs")
    .select("id, statut, progression, storage_path, erreur, contenu, created_at, updated_at")
    .eq("veille_id", veilleId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return error ? null : data;
}

// Enregistre la matrice éditée (donnée structurée) sur le job
export async function saveMatrice(jobId, contenu) {
  const { error } = await supabase.rpc("save_matrice", { p_job: jobId, p_contenu: contenu });
  return { error };
}

// Demande la régénération du .docx depuis la matrice éditée (poller, sans IA)
export async function requestRerender(jobId) {
  const { error } = await supabase.rpc("request_rerender", { p_job: jobId });
  return { error };
}

// « Lancer la veille maintenant » : crée un job de run (collecte, puis rapport à venir)
export async function requestRun(veilleId) {
  const { data, error } = await supabase.rpc("request_run", { p_veille: veilleId });
  return { jobId: data || null, error };
}

// État du dernier job de run (le front polle pendant l'exécution)
export async function fetchRunJob(veilleId) {
  const { data, error } = await supabase
    .from("run_jobs")
    .select("id, statut, progression, mentions, erreur, created_at")
    .eq("veille_id", veilleId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return error ? null : data;
}

// Demande de génération d'une Matrice Veille IA (job traité par le moteur)
export async function requestMatrice(veilleId) {
  const { data, error } = await supabase.rpc("request_matrice", { p_veille: veilleId });
  return { jobId: data || null, error };
}

// Demande d'analyse IA d'une demande de veille (intake) — job traité par le moteur headless
export async function requestAnalyse(demande, client) {
  const { data, error } = await supabase.rpc("request_analyse", {
    p_demande: demande,
    p_client: client || null,
  });
  return { jobId: data || null, error };
}

// État d'un job d'analyse (le front polle jusqu'à termine/echec)
export async function fetchAnalyseJob(jobId) {
  const { data, error } = await supabase
    .from("analyse_jobs")
    .select("id, statut, resultat, erreur")
    .eq("id", jobId)
    .maybeSingle();
  return error ? null : data;
}

// URL signée de téléchargement de la matrice générée (bucket mapping)
export async function downloadMatrice(job) {
  const path = (job.storage_path || "").replace(/^mapping\//, "");
  const { data, error } = await supabase.storage.from("mapping").createSignedUrl(path, 3600);
  return error || !data ? null : data.signedUrl;
}

// URL signée de téléchargement d'un rapport + trace dans report_downloads
export async function downloadReport(report, profileId, orgId) {
  const path = (report.storage_path || "").replace(/^rapports\//, "");
  const { data, error } = await supabase.storage.from("rapports").createSignedUrl(path, 3600);
  if (error || !data) return null;
  if (profileId && orgId) {
    // Journal best-effort — l'échec ne bloque pas le téléchargement
    supabase.from("report_downloads").insert({
      organisation_id: orgId, report_id: report.id, user_id: profileId,
    }).then(() => {});
  }
  return data.signedUrl;
}

export function VeillesProvider({ children }) {
  const { session, org, profile } = useSession();
  const orgId = org && org.id;
  const [veilles, setVeilles] = useState([]);
  const [loading, setLoading] = useState(true);

  const charger = useCallback(async () => {
    if (!session) { setVeilles([]); setLoading(false); return; }
    setLoading(true);
    const [{ data }, { data: statsRows }] = await Promise.all([
      supabase
        .from("veilles")
        .select("id, nom, client, secteur, statut, frequence, format, mots_cles, prochaine_execution_at, veille_canaux(canal_id)")
        .order("created_at"),
      supabase.from("veille_stats").select("*"),
    ]);
    const statsById = {};
    (statsRows || []).forEach((s) => { statsById[s.veille_id] = s; });
    setVeilles((data || []).map((row) => mapRow(row, statsById[row.id])));
    setLoading(false);
  }, [session, orgId]); // eslint-disable-line

  useEffect(() => { charger(); }, [charger]);

  const getVeille = (id) => veilles.find((v) => v.id === id);

  const updateVeille = (id, patch) => {
    setVeilles((prev) => prev.map((v) => (v.id === id ? { ...v, ...patch } : v)));
    const dbPatch = {};
    if (patch.nom !== undefined) dbPatch.nom = patch.nom;
    if (patch.statut !== undefined) dbPatch.statut = patch.statut;
    if (Object.keys(dbPatch).length) supabase.from("veilles").update(dbPatch).eq("id", id);
    // Les canaux (table de liaison) restent locaux pour l'instant.
  };

  // Édition complète d'une veille : persiste TOUT (mots-clés, canaux, format, fréquence,
  // destinataires, mapping) via la RPC update_veille_complete, puis recharge depuis la base.
  const updateVeilleComplete = async (veilleId, data = {}) => {
    const { error } = await supabase.rpc("update_veille_complete", {
      p_veille: veilleId,
      p_nom: data.nom,
      p_client: data.client || null,
      p_secteur: data.secteur || null,
      p_demande: data.demande || null,
      p_mots_cles: data.motsCles || [],
      p_format: data.format || "synthese",
      p_frequence: data.frequence || null,
      p_heure: data.heure || null,
      p_canaux: data.canaux || [],
      p_sources: data.sources || [],
      p_jours: data.jours || [],
      p_destinataires: data.destinataires || [],
      p_mapping: data.mapping || null,
    });
    if (!error) {
      setVeilles((prev) => prev.map((v) => (v.id === veilleId ? { ...v, ...data } : v)));
      charger();
    }
    return { error };
  };

  const pauseVeille = (id) =>
    setVeilles((prev) =>
      prev.map((v) => {
        if (v.id !== id) return v;
        // Bascule uniquement entre active et en_pause ; les autres statuts sont inchangés
        const ns = v.statut === "active" ? "en_pause" : v.statut === "en_pause" ? "active" : v.statut;
        if (ns !== v.statut) supabase.from("veilles").update({ statut: ns }).eq("id", id);
        return { ...v, statut: ns };
      })
    );

  // Création d'une veille (statut « programmée »), persistée via la RPC create_veille
  // (SECURITY DEFINER : contourne le refus d'INSERT direct sous RLS, tout en vérifiant les droits).
  // La config fine (canaux, sources, jours, destinataires…) reste locale pour la session.
  const addVeille = async (data = {}) => {
    const base = {
      slug: undefined,
      nom: data.nom,
      client: data.client || "",
      secteur: data.secteur || "",
      demande: data.demande || "",
      motsCles: data.motsCles || [],
      canaux: data.canaux || [],
      sources: data.sources || [],
      format: data.format,
      frequence: data.frequence,
      jours: data.jours || [],
      destinataires: data.destinataires || [],
      heure: data.heure,
      statut: "programmee",
      responsable: (profile && (profile.full_name || profile.email)) || "—",
      prochaineDate: "À programmer",
      mentions: 0,
      sentiment: { positif: 0, neutre: 0, negatif: 0 },
      couverture: 0,
      retombees: [],
    };

    if (orgId) {
      // Création atomique complète : canaux, sources, jours, destinataires et
      // document de mapping sont désormais persistés (RPC create_veille_complete).
      const { data: newId, error } = await supabase.rpc("create_veille_complete", {
        p_org: orgId,
        p_nom: data.nom,
        p_client: data.client || null,
        p_secteur: data.secteur || null,
        p_demande: data.demande || null,
        p_mots_cles: data.motsCles || [],
        p_format: data.format || "synthese",
        p_frequence: data.frequence || null,
        p_heure: data.heure || null,
        p_canaux: data.canaux || [],
        p_sources: data.sources || [],
        p_jours: data.jours || [],
        p_destinataires: data.destinataires || [],
        p_mapping: data.mapping || null,
      });
      if (error) return { id: null, error };
      setVeilles((prev) => [{ ...base, id: newId }, ...prev]);
      return { id: newId, error: null };
    }

    // Repli démo (organisation non chargée) : ajout local uniquement
    const tempId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `local-${Date.now()}`;
    setVeilles((prev) => [{ ...base, id: tempId }, ...prev]);
    return { id: tempId, error: null };
  };

  return (
    <VeillesContext.Provider value={{ veilles, loading, getVeille, updateVeille, updateVeilleComplete, pauseVeille, addVeille, refresh: charger }}>
      {children}
    </VeillesContext.Provider>
  );
}

// Repli neutre si le provider n'enveloppe pas l'arbre (aucune donnée de démonstration).
export function useVeilles() {
  return (
    useContext(VeillesContext) || {
      veilles: [],
      loading: false,
      getVeille: () => undefined,
      updateVeille: () => {},
      updateVeilleComplete: async () => ({ error: null }),
      pauseVeille: () => {},
      addVeille: async () => ({ id: null, error: null }),
      refresh: () => {},
    }
  );
}
